import * as React from 'react'
import { clsx } from 'clsx'
import { useUsernameFromId } from '~/hooks/use-username-from-id'
import {
  SimpleTooltip,
  SimpleTooltipProps,
} from '~/sotano-kit/primitives/tooltip'
import { BlockMilestone } from '@basehub/data/replicache/index'

export const ContributorName = ({
  id,
  withComma,
  short,
}: {
  id: string
  withComma?: boolean
  short?: boolean
}) => {
  const { username } = useUsernameFromId(id, { short })
  return (
    <span className={clsx(withComma && 'mr-1')}>
      {username}
      {withComma && ','}
    </span>
  )
}

export const ContributorsTooltip = ({
  blockMilestone,
  children,
  content,
  ...props
}: {
  blockMilestone: BlockMilestone | null
  children: React.ReactNode
} & SimpleTooltipProps) => {
  const contributorGroups = React.useMemo(() => {
    if (!blockMilestone?.contributors) {
      return []
    }

    const groups = []

    if (
      blockMilestone.currentStatus !== 'deleted' &&
      blockMilestone.contributors.created?.[0]
    ) {
      groups.push({
        type: 'Created',
        contributors: [blockMilestone.contributors.created[0]],
      })
    }

    if (
      blockMilestone.currentStatus === 'modified' &&
      blockMilestone.contributors.modified?.length
    ) {
      const filtered = blockMilestone.contributors.modified.slice(0, 3)
      groups.push({
        type: 'Modified',
        contributors: filtered,
        rest:
          blockMilestone.contributors.modified.length > 3
            ? blockMilestone.contributors.modified.length - 3
            : 0,
      })
    }

    if (
      blockMilestone.currentStatus === 'deleted' &&
      blockMilestone.contributors.deleted?.[0]
    ) {
      groups.push({
        type: 'Deleted',
        contributors: [blockMilestone.contributors.deleted[0]],
      })
    }

    return groups
  }, [blockMilestone?.contributors, blockMilestone?.currentStatus])

  return (
    <SimpleTooltip
      content={
        <p
          className={clsx(
            'first-letter:uppercase text-center text-pretty',
            contributorGroups.length && 'text-dark-faint dark:text-dark-muted'
          )}
        >
          {contributorGroups.length > 0 ? (
            <>
              {contributorGroups.map((group, groupIdx) => (
                <span key={group.type}>
                  {groupIdx > 0 && <br />}
                  {group.type} by{' '}
                  <span className="text-dark-bg">
                    {group.contributors.map((contributorId, idx, arr) => (
                      <ContributorName
                        key={contributorId}
                        id={contributorId}
                        short
                        withComma={idx < arr.length - 1}
                      />
                    ))}
                    {group.rest ? ` and ${group.rest} more` : ''}
                  </span>
                </span>
              ))}
            </>
          ) : (
            content
          )}
        </p>
      }
      {...props}
    >
      {children}
    </SimpleTooltip>
  )
}
